import Prism from "prismjs";
import _times from "lodash/times";
import _keys from "lodash/keys";
import _includes from "lodash/includes";
import copy from "clipboard-copy";
import md5 from "md5";
import { saveAs } from "file-saver";

import globalDeselect from "./util/global-deselect";
import Key from "./util/key";
import * as analytics from "./util/segment";

const DELIMITER = "****";
const COPY = '<i class="far fa-copy"></i>';
const COPIED = 'Copied<i class="fas fa-check"></i>';

// Prismjs implicitly enables code highlighting on import. This workaround
// allows us to disable that behavior and imperatively enable highlighting later
// on.
document.removeEventListener("DOMContentLoaded", Prism.highlightAll);

const PROMPT_TYPES = {
    memsql: {
        val: "memsql>",
        multilineVal: "->",
        highlighter: Prism.languages.sql,
    },
    "user-shell": {
        val: "$",
        multilineVal: "",
        highlighter: Prism.languages.shell,
    },
    "root-shell": {
        val: "#",
        multilineVal: "",
        highlighter: Prism.languages.shell,
    },
};

function highlightAll() {
    // Allow prism to style language-less code blocks
    $("code:not([class])").addClass("language-text");
    Prism.languages.text = {};
    Prism.languages.memsql = Prism.languages.sql;

    _keys(PROMPT_TYPES).forEach(key => {
        Prism.languages[key] = PROMPT_TYPES[key].highlighter;
    });

    Prism.highlightAll();
}

// Splits codeblocks into input and output sections with a special delimiter
function splitInputOutput() {
    const $codeblocks = $("pre");

    $.each($codeblocks, (i, pre) => {
        const $pre = $(pre);
        const $code = $pre.find("code");
        const blocks = $code.html().split(DELIMITER);

        if (blocks.length > 1) {
            const input = blocks[0].trim();
            const output = blocks
                .slice(1, blocks.length)
                .join(DELIMITER)
                .trim();
            const $outputPre = $("<pre>", { class: "language-text output" });
            const $outputCode = $("<code>", { class: "language-text" });

            $pre.addClass("input");
            $code.html(input);

            $outputCode.html(output);
            $outputPre.append($outputCode).insertAfter($pre);

            if (blocks.length > 2) {
                console.warn(
                    `More than one delimiter "****" found in the following code block:\n${input}\n${DELIMITER}\n${output}`
                );
            }
        }
    });
}

function addPrompt() {
    function buildPrompt({ prompt, numberOfLines, element }) {
        const $promptWrapper = $("<div>", { class: "prompt-wrapper" });

        _times(numberOfLines, i => {
            const promptValue = i === 0 ? prompt.val : prompt.multilineVal;
            const $prompt = $("<span>", { class: "prompt" }).attr(
                "data-prompt",
                promptValue
            );

            $promptWrapper.append($prompt);
        });

        $(element)
            .parents("pre")
            .prepend($promptWrapper);
    }

    Prism.hooks.add("before-highlight", env => {
        const $code = $(env.element);

        env.numberOfLines = env.code.trim().split("\n").length;

        if ($code.parent().is("pre")) {
            const classes = $code.attr("class").split(" ");

            classes.forEach(className => {
                const type = className.replace("language-", "");

                if (_includes(_keys(PROMPT_TYPES), type)) {
                    env.prompt = PROMPT_TYPES[type];
                }
            });
        }
    });

    Prism.hooks.add("complete", ({ prompt, numberOfLines, element }) => {
        if (prompt) {
            buildPrompt({
                prompt,
                numberOfLines,
                element,
            });
        }
    });
}

// Wrapper to house buttons in the upper right corner of each code block
function addToolbar() {
    $("pre:not(.in-modal)").each((i, pre) => {
        const $template = $(`
            <div class="pre-wrapper">
                <div class="pre-wrapper-inner">
                    <div class="code-block-toolbar"></div> 
                </div>
            </div>
        `);
        $template.insertAfter(pre);
        $template.find(".pre-wrapper-inner").prepend(pre);
    });

    Prism.hooks.add("before-highlight", ({ code, element }) => {
        const $preWrapper = $(element).parents(".pre-wrapper");
        const id = md5(code).substring(0, 40);

        $preWrapper.attr("id", id);
    });
}

function copyToClipboard() {
    function buildBtn() {
        const $copyBtn = $("<div>", { class: "copy-btn" });
        $copyBtn.append(COPY);
        return $copyBtn;
    }

    Prism.hooks.add("before-highlight", ({ code, element }) => {
        const $pre = $(element).parent();
        const $copyBtn = buildBtn();
        const $toolbar = $pre.siblings(".code-block-toolbar");

        if ($pre.hasClass("output")) {
            return;
        }

        $toolbar.prepend($copyBtn);

        $copyBtn.click(() => {
            copyCode({ code, $toolbar, $copyBtn });
            analytics.track({
                action: "click-copy-button",
                category: "code-block",
                label: getCodeblockId(element),
            });
        });
    });
}

function copyCode({ code, $toolbar, $copyBtn }) {
    copy(code.trim());

    // Only ever show one "Copied" at a time on the page.
    const $copyBtns = $(".code-block-toolbar .copy-btn");
    $.each($copyBtns, (i, btn) => {
        if (btn !== $copyBtn[0]) {
            updateCopyBtn({
                el: btn,
                $toolbar: $(btn).parents(".code-block-toolbar"),
                copied: false,
            });
        }
    });

    updateCopyBtn({
        el: $copyBtn[0],
        $toolbar,
        copied: true,
    });

    setTimeout(() => {
        updateCopyBtn({
            el: $copyBtn[0],
            $toolbar,
            copied: false,
        });
    }, 3000);
}

function updateCopyBtn({ el, $toolbar, copied }) {
    const TRANSITION_DELAY = 150;

    $toolbar.toggleClass("is-visible", copied);

    function updateInner() {
        const val = copied ? COPIED : COPY;
        $(el).html(val);
        $(el).toggleClass("copied", copied);
    }

    // Updates the button AFTER the toolbar is no longer visible
    // This fixes the jank where you can see copy icon flash back as it fades out.
    if (copied) {
        updateInner();
    } else {
        setTimeout(updateInner, TRANSITION_DELAY);
    }
}

// Expects the code tag DOM node and gets the id on its pre-wrapper element
function getCodeblockId(codeEl) {
    return $(codeEl)
        .parents(".pre-wrapper")
        .attr("id");
}

function openInModal() {
    const $modal = $(".modal.code");
    const $modalInner = $(".code-modal-inner");
    const $modalCode = $(".modal.code code");
    const $modalToolbar = $(".modal.code .code-block-toolbar");
    const $downloadBtn = $(".modal.code .download-btn");
    const $closeBtn = $(".modal.code .close-btn");

    function buildOpenBtn({ code, element }) {
        const $openBtn = $("<div>", { class: "open-modal-btn" });
        $openBtn.append("<i class='far fa-expand-alt'></i>");
        $openBtn.click(() => {
            openModal({ code, element });
            analytics.track({
                action: "click-open-modal-button",
                category: "code-block",
                label: getCodeblockId(element),
            });
        });
        return $openBtn;
    }

    function setHash({ element }) {
        window.location.hash = $(element)
            .parents(".pre-wrapper")
            .attr("id");
    }

    function removeHash() {
        const { history, location: { pathname, search } } = window;
        history.replaceState("", document.title, pathname + search);
    }

    // On load, if URL contains hash of a code block, expand that code block
    // into a modal.
    function openModalOnHash({ code, element }) {
        const $id = $(element)
            .parents(".pre-wrapper")
            .attr("id");
        const $hash = window.location.hash.substring(1);

        if ($id === $hash) {
            openModal({ code, element });
        }
    }

    function closeModal() {
        $("body").removeClass("modal-open");
        $modal.removeClass("is-visible");

        // When a "select all" is done within the modal, then the modal closes,
        // the selection transfers to everything on the page. This clears the
        // selection.
        globalDeselect();
        removeHash();
    }

    function openModal({ code, element }) {
        const $copyBtn = $(".modal.code .copy-btn");

        // Code blocks that contain angle brackets inside of a modal do not
        // get parsed correctly for some reason. Therefore, we need to replace
        // these characters with their corresponding HTML entities.
        const cleanCodeBlock = code.replace(/<(.*?)>/g, "&lt;$1&gt;");

        $("body").addClass("modal-open");
        $modal.addClass("is-visible");
        $modalCode.html(cleanCodeBlock);

        $copyBtn.off("click");
        $copyBtn.click(() => {
            copyCode({
                code,
                $copyBtn,
                $toolbar: $modalToolbar,
            });
            analytics.track({
                action: "click-copy-button-in-modal",
                category: "code-block",
                label: getCodeblockId(element),
            });
        });

        $downloadBtn.off("click");
        $downloadBtn.click(() => {
            const blob = new Blob([code], { type: "text/plain;charset=utf-8" });
            saveAs(blob, "snippet.txt");
            analytics.track({
                action: "click-download-button",
                category: "code-block",
                label: getCodeblockId(element),
            });
        });

        $closeBtn.off("click");
        $closeBtn.click(() => {
            closeModal();
            analytics.track({
                action: "click-close-modal-button",
                category: "code-block",
                label: getCodeblockId(element),
            });
        });

        // Close modal when clicks are detected outside of modal
        $modal.click(() => {
            closeModal();
        });

        $modalInner.click(evt => {
            evt.stopPropagation();
        });

        // Close modal on ESC key
        $(document).keyup(function(evt) {
            if (evt.keyCode === Key.ESC) {
                closeModal();
                analytics.track({
                    action: "press-esc-key",
                    category: "code-block",
                    label: getCodeblockId(element),
                });
            }
        });

        setHash({ element });
    }

    Prism.hooks.add("before-highlight", ({ code, element }) => {
        const $pre = $(element).parents("pre");

        if (!$pre.hasClass("in-modal") && !$pre.hasClass("output")) {
            const $toolbar = $pre.siblings(".code-block-toolbar");
            const $openBtn = buildOpenBtn({ code, element });

            $toolbar.append($openBtn);
        }

        openModalOnHash({ code, element });
    });
}

function trackClicksOnCode() {
    $('code').each((i, el) => {
        $(el).click(() => {
            const id = getCodeblockId(el);
    
            analytics.track({
                action: "click-code-block",
                category: "code-block",
                label: getCodeblockId(el),
            });
        });
    })
}

function initCodeBlockFeatures() {
    addToolbar();
    splitInputOutput();
    addPrompt();
    copyToClipboard();
    openInModal();
    highlightAll();
    trackClicksOnCode();
}

export { initCodeBlockFeatures };



// WEBPACK FOOTER //
// ./themes/docs/static/js/syntax.js